import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CssBaseline,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { ThemeProvider, styled } from "@mui/material/styles";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import axiosInstance from "../../api/axiosConfig";
import theme from "../../theme";
import { isUserAuthenticated, getStoredRoles } from "../../utils/authSession";
import { getDefaultAuthorizedPath } from "../../utils/roleAccess";
import * as C from "../../constants";
import {
  formatLoginSchoolLabel,
  saveSelectedLoginSchool,
  readSelectedLoginSchool,
} from "./loginSchoolSelection";

const Page = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
  padding: theme.spacing(3),
  background:
    "radial-gradient(circle at 12% 12%, rgba(5,150,105,0.15), transparent 34%), linear-gradient(180deg, #f0fdf4 0%, #f8fafc 100%)",
}));

const Panel = styled(Box)(({ theme }) => ({
  width: "min(100%, 640px)",
  padding: theme.spacing(4),
  borderRadius: 28,
  background: "rgba(255,255,255,0.92)",
  border: "1px solid rgba(148,163,184,0.22)",
  boxShadow: "0 24px 70px -46px rgba(15,23,42,0.35)",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2.5),
    borderRadius: 6,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    minHeight: 58,
    borderRadius: 6,
    backgroundColor: "#ffffff",
    "&.Mui-focused": {
      boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.11)}`,
    },
  },
}));

const SchoolCard = styled(Paper)(({ theme, selected }) => ({
  padding: theme.spacing(2),
  borderRadius: 6,
  cursor: "pointer",
  border: selected
    ? `2px solid ${theme.palette.primary.main}`
    : "1px solid rgba(203, 213, 225, 0.8)",
  backgroundColor: selected
    ? alpha(theme.palette.primary.main, 0.06)
    : "#ffffff",
  transition: "all 0.2s ease-in-out",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    transform: "translateY(-1px)",
  },
}));

const SchoolSelectPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(() => readSelectedLoginSchool());
  const [schoolSearchInput, setSchoolSearchInput] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isUserAuthenticated()) {
      navigate(getDefaultAuthorizedPath(getStoredRoles()) || C.ROUTES.DASHBOARD, { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const query = searchParams.get("code") || searchParams.get("school") || "";
    if (query) {
      setSchoolSearchInput(query);
    }
  }, [searchParams]);

  useEffect(() => {
    let active = true;
    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get("/auth/schools/search", {
          params: schoolSearchInput.trim() ? { q: schoolSearchInput.trim() } : {},
          skipAuthHeaders: true,
          skipAuthRedirect: true,
          skipGlobalError: true,
        });
        if (active) {
          const list = Array.isArray(response.data) ? response.data : [];
          setSchoolOptions(list);

          // Auto-select matching school code from query param if available
          const paramCode = (searchParams.get("code") || searchParams.get("school") || "").toUpperCase();
          if (paramCode) {
            const match = list.find((s) => (s.schoolCode || "").toUpperCase() === paramCode);
            if (match) {
              setSelectedSchool(match);
            }
          }
        }
      } catch (error) {
        if (active) setSchoolOptions([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 180);

    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [schoolSearchInput, searchParams]);

  const handleSelectSchool = (school) => {
    if (!school?.schoolCode) return;
    setSelectedSchool(school);
    saveSelectedLoginSchool(school);
    navigate("/login", { replace: true });
  };

  const continueToLogin = () => {
    if (!selectedSchool?.schoolCode) return;
    saveSelectedLoginSchool(selectedSchool);
    navigate("/login", { replace: true });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Page>
        <Panel>
          <Stack spacing={3}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 4,
                display: "grid",
                placeItems: "center",
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            >
              <SchoolRoundedIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: { xs: "1.95rem", sm: "2.35rem" }, fontWeight: 850, letterSpacing: "-0.04em", color: "#0f172a" }}>
                Select your school
              </Typography>
              <Typography sx={{ mt: 1, color: "#64748b", fontSize: "0.98rem", lineHeight: 1.65 }}>
                Pick your school to continue to sign in. We will save your choice so you don't need to re-enter a school code next time.
              </Typography>
            </Box>

            <Autocomplete
              fullWidth
              options={schoolOptions}
              loading={loading}
              value={selectedSchool}
              inputValue={schoolSearchInput}
              filterOptions={(x) => x}
              onInputChange={(_, value) => {
                setSchoolSearchInput(value);
              }}
              onChange={(_, value) => {
                if (value) handleSelectSchool(value);
              }}
              getOptionLabel={(option) => (typeof option === "string" ? option : formatLoginSchoolLabel(option))}
              isOptionEqualToValue={(option, value) => option?.schoolCode === value?.schoolCode}
              noOptionsText={schoolSearchInput.trim() ? "No schools found" : "Search by school name or code"}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    borderRadius: 1.5,
                    background: "linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)",
                    border: "1px solid rgba(5,150,105,0.2)",
                    boxShadow: "0 18px 44px -26px rgba(15,23,42,0.36)",
                  },
                },
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.schoolCode || option.schoolId}>
                  <Stack spacing={0.2} sx={{ width: "100%" }}>
                    <Typography sx={{ fontWeight: 750, color: "#0f172a" }}>{option.schoolName || option.name}</Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: "#64748b" }}>Code: {option.schoolCode}</Typography>
                  </Stack>
                </Box>
              )}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  label="School Search"
                  placeholder="Search by school name or code (e.g. MARK)"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchRoundedIcon sx={{ color: "#94a3b8" }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Quick Available Schools Cards */}
            {schoolOptions.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#64748b", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Available Schools ({schoolOptions.length})
                </Typography>
                <Stack spacing={1.5}>
                  {schoolOptions.map((school) => {
                    const isSelected = selectedSchool?.schoolCode === school.schoolCode;
                    return (
                      <SchoolCard
                        key={school.schoolCode || school.schoolId}
                        selected={isSelected}
                        onClick={() => handleSelectSchool(school)}
                        elevation={0}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 750, fontSize: "1.05rem", color: "#0f172a" }}>
                            {school.schoolName || school.name}
                          </Typography>
                          <Typography sx={{ fontSize: "0.82rem", color: "#64748b", mt: 0.2 }}>
                            School Code: <strong>{school.schoolCode}</strong>
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {isSelected && <CheckCircleRoundedIcon color="primary" />}
                          <Button
                            size="small"
                            variant={isSelected ? "contained" : "outlined"}
                            sx={{ borderRadius: 6, textTransform: "none", fontWeight: 700 }}
                          >
                            Select & Sign In
                          </Button>
                        </Stack>
                      </SchoolCard>
                    );
                  })}
                </Stack>
              </Box>
            )}

            <Button
              size="large"
              variant="contained"
              endIcon={<ArrowForwardRoundedIcon />}
              disabled={!selectedSchool?.schoolCode}
              onClick={continueToLogin}
              sx={{
                alignSelf: "flex-start",
                px: 3.5,
                py: 1.3,
                borderRadius: 6,
                textTransform: "none",
                fontWeight: 800,
                boxShadow: "none",
                background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
              }}
            >
              Continue to login
            </Button>
          </Stack>
        </Panel>
      </Page>
    </ThemeProvider>
  );
};

export default SchoolSelectPage;

